import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---------- JWT fallback for browsers/PWAs that block third-party cookies ----------
// Samsung Internet (especially in installed-PWA mode) and iOS Safari ITP can refuse
// to store/send the SameSite=None auth cookie, causing a login loop. To survive
// this we ALSO persist the JWT in localStorage and attach it as Authorization: Bearer
// on every request. The backend `get_current_user` already accepts either source.
const TOKEN_KEY = "gwg_auth_token";

export function getAuthToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}
export function setAuthToken(token) {
  try { if (token) localStorage.setItem(TOKEN_KEY, token); } catch { /* private mode */ }
}
export function clearAuthToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

const api = axios.create({ baseURL: API, withCredentials: true });

// Attach Authorization header from localStorage on every request (cookie still tried in
// parallel via withCredentials). Either path is enough for the backend to authenticate.
api.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// If the server says our token is invalid/expired, drop the stale localStorage copy so
// the next page load shows the login screen cleanly (instead of looping).
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) clearAuthToken();
    return Promise.reject(err);
  }
);

export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const LEVEL_ORDER = ["Wood", "Stone", "Brass", "Silver", "Gold", "Platinum", "Diamond"];
export const LEVEL_COLORS = {
  Wood: "#8B5A2B", Stone: "#A3A3A3", Brass: "#CD7F32",
  Silver: "#C0C0C0", Gold: "#FFD700", Platinum: "#E5E4E2", Diamond: "#B9F2FF",
};

export const ALARM_SOUNDS = [
  { id: "beep", name: "Beep", freq: 880, type: "sine" },
  { id: "chime", name: "Chime", freq: 660, type: "triangle" },
  { id: "bell", name: "Bell", freq: 1046, type: "sine" },
  { id: "buzz", name: "Buzzer", freq: 220, type: "square" },
  { id: "ping", name: "Ping", freq: 1318, type: "sine" },
];

// ---------- Audio context (iOS-safe) ----------
// iOS Safari starts the AudioContext in 'suspended' state and only allows
// resume() from within a real user gesture. Creating the context lazily on
// first interaction + resume()-ing keeps the alarm reliable on iPhones.
let _audioCtx = null;
let _audioUnlocked = false;

function getAudioCtx() {
  if (!_audioCtx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    _audioCtx = new Ctor();
  }
  return _audioCtx;
}

export function unlockAudio() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  // Play a 1-frame silent buffer to fully unlock on iOS <14.5 too.
  try {
    if (ctx.state === "suspended") ctx.resume();
    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
  } catch (_) { /* ignore */ }
  _audioUnlocked = true;
}

// Auto-attach a one-shot unlock on the very first user gesture anywhere in
// the app. This is the standard iOS pattern. Safe to call on Android too.
if (typeof window !== "undefined") {
  const onFirstGesture = () => {
    unlockAudio();
    window.removeEventListener("touchstart", onFirstGesture, true);
    window.removeEventListener("touchend", onFirstGesture, true);
    window.removeEventListener("mousedown", onFirstGesture, true);
    window.removeEventListener("keydown", onFirstGesture, true);
  };
  window.addEventListener("touchstart", onFirstGesture, { capture: true, passive: true });
  window.addEventListener("touchend", onFirstGesture, { capture: true, passive: true });
  window.addEventListener("mousedown", onFirstGesture, { capture: true });
  window.addEventListener("keydown", onFirstGesture, { capture: true });
}

export function playAlarm(soundId = "beep") {
  try {
    const sound = ALARM_SOUNDS.find((s) => s.id === soundId) || ALARM_SOUNDS[0];
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      // Best-effort resume in case unlock didn't happen yet.
      try { ctx.resume(); } catch (_) {}
    }
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = sound.type;
    o.frequency.value = sound.freq;
    o.connect(g); g.connect(ctx.destination);
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
    o.start(t);
    o.stop(t + 1.0);
  } catch (e) { /* ignore */ }
}
